// Constants
export const DEFAULT_LANGUAGE = 'en';
export const DEFAULT_CATEGORY = 'UTILITY';
export const COMPONENT_TYPES = {
  HEADER: 'HEADER',
  BODY: 'BODY',
  BUTTONS: 'BUTTONS',
};
export const MEDIA_FORMATS = ['IMAGE', 'VIDEO', 'DOCUMENT'];

export const findComponentByType = (template, type) =>
  template.components?.find(component => component.type === type);

export const processVariable = str => {
  return str.replace(/{{|}}/g, '');
};

export const allKeysRequired = value => {
  const keys = Object.keys(value);
  return keys.every(key => value[key]);
};

export const replaceTemplateVariables = (templateText, processedParams) => {
  return templateText.replace(/{{([^}]+)}}/g, (match, variable) => {
    const variableKey = processVariable(variable);
    return processedParams.body?.[variableKey] || `{{${variable}}}`;
  });
};

/**
 * Builds default processed_params from an approved template.
 * Header media (IMAGE/VIDEO/DOCUMENT) and LOCATION headers are inferred from the template definition.
 *
 * For AUTHENTICATION templates Meta requires the same OTP value in the body {{1}} parameter
 * and in every URL/OTP button parameter (Meta converts OTP buttons to URL type upon approval).
 * We return a special `otp` key under `auth` so the parser can expose a single input field.
 */
export const buildTemplateParameters = template => {
  const allVariables = {};

  const bodyComponent = findComponentByType(template, COMPONENT_TYPES.BODY);
  const headerComponent = findComponentByType(template, COMPONENT_TYPES.HEADER);

  if (!bodyComponent) return allVariables;

  const isAuthentication = template.category === 'AUTHENTICATION';

  if (isAuthentication) {
    // Auth templates: single OTP shared across body and all URL/OTP buttons.
    allVariables.auth = { otp: '' };
    return allVariables;
  }

  const templateString = bodyComponent.text;

  // Process body variables
  const matchedVariables = templateString.match(/{{([^}]+)}}/g);
  if (matchedVariables) {
    allVariables.body = {};
    matchedVariables.forEach(variable => {
      const key = processVariable(variable);
      allVariables.body[key] = '';
    });
  }

  const headerFmt = headerComponent?.format?.toUpperCase();
  if (headerComponent && MEDIA_FORMATS.includes(headerFmt)) {
    if (!allVariables.header) allVariables.header = {};
    allVariables.header.media_url = '';
    allVariables.header.media_type = headerComponent.format.toLowerCase();

    if (headerComponent.format.toLowerCase() === 'document') {
      allVariables.header.media_name = '';
    }
  } else if (headerComponent && headerFmt === 'LOCATION') {
    if (!allVariables.header) allVariables.header = {};
    allVariables.header.latitude = '';
    allVariables.header.longitude = '';
    allVariables.header.name = '';
    allVariables.header.address = '';
  }

  // Process button variables
  const buttonComponents = template.components.filter(
    component => component.type === COMPONENT_TYPES.BUTTONS
  );

  buttonComponents.forEach(buttonComponent => {
    if (buttonComponent.buttons) {
      buttonComponent.buttons.forEach((button, index) => {
        // Handle URL buttons with variables
        if (button.type === 'URL' && button.url && button.url.includes('{{')) {
          const buttonVars = button.url.match(/{{([^}]+)}}/g) || [];
          if (buttonVars.length > 0) {
            if (!allVariables.buttons) allVariables.buttons = [];
            allVariables.buttons[index] = {
              type: 'url',
              parameter: '',
              url: button.url,
              variables: buttonVars.map(v => processVariable(v)),
            };
          }
        }

        // Handle copy code buttons
        if (button.type === 'COPY_CODE') {
          if (!allVariables.buttons) allVariables.buttons = [];
          allVariables.buttons[index] = {
            type: 'copy_code',
            parameter: '',
          };
        }
      });
    }
  });

  return allVariables;
};
